import React from "react";
import { useSelector } from "react-redux";
import {
  getAuctionPenById,
  getNaturallyOrderedAuctionPenIdsByRoundId,
  getSellingOrderedAuctionPenIdsByRoundId,
  selectSaleLotIdsByAuctionPenIdLookup,
} from "selectors";
import { pluralize } from "lib/pluralize";
import { getOutOfOrderValues } from "lib/arrayUtils";
import { FilterType } from "constants/filters";
import { InformationBubble } from "components/InformationBubble";
import { applyFilter, PenRangeFilter } from "./processData";

interface AuctionPenSpanMessageProps {
  startPenId: string | null;
  endPenId: string | null;
  roundId: number | null;
  id: string;
}

export function AuctionPenSpanMessage(
  props: AuctionPenSpanMessageProps,
): React.JSX.Element {
  const { startPenId, endPenId, roundId, id } = props;

  const startPen: AuctionPen | null = useSelector(
    getAuctionPenById(startPenId),
  );
  const endPen: AuctionPen | null = useSelector(getAuctionPenById(endPenId));

  const allSellingOrderedPenIds: string[] =
    useSelector(getSellingOrderedAuctionPenIdsByRoundId(roundId)) || [];

  const allNaturallyOrderedPenIds: string[] =
    useSelector(getNaturallyOrderedAuctionPenIdsByRoundId(roundId)) || [];

  const saleLotIdsBySellingPenIdLookup =
    useSelector(selectSaleLotIdsByAuctionPenIdLookup) || {};

  const sellingOrderedPenIds: string[] = allSellingOrderedPenIds.filter(
    (auctionPenId: string) => {
      const saleLotIds = saleLotIdsBySellingPenIdLookup[auctionPenId] || [];
      return saleLotIds.length > 0;
    },
  );

  const naturalOrderedPenIds: string[] = allNaturallyOrderedPenIds.filter(
    (auctionPenId: string) => {
      const saleLotIds = saleLotIdsBySellingPenIdLookup[auctionPenId] || [];
      return saleLotIds.length > 0;
    },
  );

  if (!startPen && !endPen) {
    return null;
  }

  if (!startPen || !endPen) {
    // When only one pen is selected, don't show any information
    return null;
  }

  const filterSetting: PenRangeFilter = {
    startPenId,
    endPenId,
    roundId,
    id,
    type: FilterType.PenRange,
  };
  const sellingOrderSlice: string[] = applyFilter(
    filterSetting,
    sellingOrderedPenIds,
  );
  const naturalOrderSlice: string[] = applyFilter(
    filterSetting,
    naturalOrderedPenIds,
  );

  const penRangeCount: number = sellingOrderSlice.length;

  const outOfOrderPenIds: string[] = getOutOfOrderValues(
    sellingOrderSlice,
    naturalOrderSlice,
  );

  const outOfNaturalOrderCount: number = outOfOrderPenIds.length;
  const messageComponents: string[] = [
    `${penRangeCount} ${pluralize("Pen", penRangeCount)} in range.`,
  ];
  if (outOfNaturalOrderCount)
    messageComponents.push(
      `${outOfNaturalOrderCount} ${pluralize("Pen", outOfNaturalOrderCount)} out of natural order.`,
    );
  const message: string = messageComponents.join(" ");

  return <InformationBubble message={message} />;
}
